/**
 * PWA helpers. Later PWA-only route/menu filters should use isStandalonePwa()
 * (or a feature-flag wrapping it). Do not hide routes based on this alone yet.
 */

const PWA_INSTALL_DISMISS_KEY = 'gt-pwa-install-dismissed';
const PWA_STANDALONE_CLASS = 'pwa-standalone';

export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const displayStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone =
    'standalone' in window.navigator &&
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return displayStandalone || iosStandalone;
}

export function isIosDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const ua = window.navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOs =
    window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
}

/** Android phone (not tablet). */
export function isAndroidPhone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const ua = window.navigator.userAgent || '';
  return /Android/i.test(ua) && /Mobile/i.test(ua);
}

/**
 * Samsung Internet mints WebAPKs that Play Protect often flags (outdated targetSdk).
 * Steer those users to install from Chrome instead of calling beforeinstallprompt.
 */
export function isSamsungInternetBrowser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return /SamsungBrowser/i.test(window.navigator.userAgent || '');
}

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

/** Mobile browser session eligible for the install popup (not installed / not dismissed). */
export function canOfferPwaInstallPrompt(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return !isStandalonePwa() && !wasPwaInstallDismissed();
}

export function wasPwaInstallDismissed(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(PWA_INSTALL_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissPwaInstallPrompt(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(PWA_INSTALL_DISMISS_KEY, '1');
  } catch {
    // ignore quota / private mode
  }
}

/** Apply `html.pwa-standalone` for installed-app layout (safe areas, mobile chrome). */
export function syncPwaStandaloneClass(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  if (isStandalonePwa()) {
    root.classList.add(PWA_STANDALONE_CLASS);
  } else {
    root.classList.remove(PWA_STANDALONE_CLASS);
  }
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  const isSecure =
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost';
  if (!isSecure) {
    return;
  }

  const register = () => {
    const swUrl = '/sw.js';
    const trustedTypes = (window as Window & {
      trustedTypes?: {
        defaultPolicy?: { createScriptURL?: (input: string) => string };
      };
    }).trustedTypes;
    const scriptURL =
      trustedTypes?.defaultPolicy?.createScriptURL?.(swUrl) ?? swUrl;
    // updateViaCache: 'none' — always revalidate /sw.js so installability + updates are reliable.
    navigator.serviceWorker
      .register(scriptURL as string, {
        scope: '/',
        updateViaCache: 'none',
      })
      .catch(() => {
        // Android install needs SW; iOS A2HS does not. Fail quietly either way.
      });
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register);
  }
}
